import { Checkbox, FormControlLabel, Grid } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import cardValidator from 'card-validator'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { FormValues } from '../../../lib/cart/types'
import { FilledTextFieldShimmer } from '../../form/FormFieldShimmer'
import { RequiredSelectField, RequiredTextField } from '../../form/RequiredFields'
import AcceptedCreditCards from './AcceptedCreditCards'
import SelectSavedCreditCard from './SelectSavedCreditCard'

const monthNames = {
  long: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
} as const

export interface PaymentFormProps {
  paymentMethod: string
  delayedBillingRequired?: boolean
}

const PaymentForm: React.FC<PaymentFormProps> = ({ paymentMethod, delayedBillingRequired }) => {
  const { control, getValues, setValue } = useFormContext<
    FormValues.CreditCard | FormValues.SavedCreditCard
  >()
  const selectedCreditCardId = useWatch<FormValues.SavedCreditCard>({
    name: 'selectedSavedCreditCardId',
  })

  const monthOptions = useMemo(
    () =>
      monthNames.long.map((name, index) => ({
        value: `${index + 1}`.padStart(2, '0'),
        label: `${index + 1}: ${name}`,
      })),
    [],
  )

  const yearOptions = useMemo(
    () =>
      new Array(15).fill(undefined).reduce(
        (
          acc: { currentYear: number; options: Array<{ label: string; value: string }> },
          _,
          index,
        ) => {
          const year = `${acc.currentYear + index}`
          acc.options.push({
            value: year,
            label: year,
          })
          return acc
        },
        { options: [], currentYear: new Date().getFullYear() },
      ).options,
    [],
  )

  const onCardNumberBlur = useCallback(() => {
    const rawValue = getValues('cardNumber') ?? ''
    const sanitizedValue = rawValue.replace(/\s+/g, '')
    if (rawValue !== sanitizedValue) {
      setValue('cardNumber', sanitizedValue, {
        shouldDirty: true,
      })
    }
  }, [getValues, setValue])

  if (paymentMethod !== 'paradoxlabs_cybersource') {
    return null
  }

  return (
    <Grid
      className="fs-exclude"
      container
      spacing={2}
      alignItems="center"
      justifyContent="center"
      item
      xs={12}
    >
      <Subscribe
        fallback={
          <>
            <Grid item xs={12}>
              <FilledTextFieldShimmer $gutterBottom />
            </Grid>
            <Grid item xs={12}>
              <FilledTextFieldShimmer $gutterBottom />
            </Grid>
          </>
        }
      >
        <SelectSavedCreditCard />
        {!selectedCreditCardId && (
          <>
            <Grid item xs={12}>
              <AcceptedCreditCards />
            </Grid>
            <Grid item xs={12}>
              <RequiredTextField
                autoComplete="cc-number"
                inputMode="numeric"
                label="Credit Card Number"
                name="cardNumber"
                data-cy="card-number"
                onBlur={onCardNumberBlur}
                optionalRules={{
                  pattern: /^[\d-]+$/,
                  validate: (value: unknown) => cardValidator.number(value).isValid,
                }}
              />
            </Grid>
            <Grid item xs={7} sm={5}>
              <RequiredSelectField
                autoComplete="cc-exp-month"
                label="Month"
                name="cardExpMonth"
                data-cy="card-exp-month"
                options={monthOptions}
              />
            </Grid>
            <Grid item xs={5} sm={3}>
              <RequiredSelectField
                autoComplete="cc-exp-year"
                label="Year"
                name="cardExpYear"
                data-cy="card-exp-year"
                options={yearOptions}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <RequiredTextField
                autoComplete="cc-csc"
                inputMode="numeric"
                label="CVV"
                name="cardCVV"
                data-cy="card-cvv"
                optionalRules={{ maxLength: 4, minLength: 3 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                defaultValue={!!delayedBillingRequired || true}
                name="saveCard"
                render={({ name, onBlur, onChange, ref, value }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        inputRef={ref}
                        onBlur={onBlur}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    name={name}
                    label="Save Credit Card After Purchase"
                    disabled={!!delayedBillingRequired}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Subscribe>
    </Grid>
  )
}

export default PaymentForm
